import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, LayoutDashboard, Workflow, SlidersHorizontal, ShieldCheck, BookOpen } from 'lucide-react';
import { InfoTip } from '../components/shared/FormKit';
import ResultCount from '../components/shared/ResultCount';
import { formatDate } from '../utils/dateFormat';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const THEAD_CLS = 'px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider';

// Permissions — one page for what every permission means AND who
// effectively holds it right now. Both halves read
// GET /api/admin/permission-coverage, whose rows are the registry rows
// (label / group / covers / flow — server/config/permissionRegistry.js is the
// single source) plus live holder data. Tabs follow the registry's groups.
//   🔴 none    — zero effective holders (nobody can review/manage this;
//                review rows also show how many requests are stuck)
//   🟡 warning — covered, but stale grants linger (holder deactivated, or
//                their staff row carries a date_left)
//   🟢 ok      — healthy
// Effective holder = active account whose email has no staff row, or maps to
// a staff row without a date_left (users↔staff strictly by email). The
// super_admin rescue account never counts.
// Read-only: grants are edited on Accounts, defaults on Roles.

// Past this many holders the names stop earning their space — the count says
// it better. Stale grants still show by name however long the list: they are
// the ones needing action, and a bare number would bury them.
const NAME_LIMIT = 5;

const STATUS = {
  none: { badge: 'bg-red-100 text-red-700', row: 'bg-red-50/60', label: 'Nobody can act' },
  warning: { badge: 'bg-amber-100 text-amber-700', row: 'bg-amber-50/40', label: 'Stale grants' },
  ok: { badge: 'bg-emerald-100 text-emerald-700', row: '', label: 'OK' },
};

// Tab icons by top-level name. A registry group not listed here still renders
// — it falls back to the book icon rather than disappearing.
const TAB_ICONS = {
  Dashboards: LayoutDashboard,
  Workflow,
  Management: SlidersHorizontal,
  System: ShieldCheck,
};

// Registry group names of the form 'Workflow — Submit' are read as
// 'top-level tab — sub tab'; a plain name is a tab with no sub tabs.
const splitGroup = (group) => {
  const parts = String(group).split(' — ');
  return parts.length > 1
    ? { top: parts[0], sub: parts.slice(1).join(' — ') }
    : { top: String(group), sub: null };
};

// Stale grants first (they need fixing), then effective holders by name.
const sortHolders = (holders) => [...holders].sort((a, b) => {
  if (a.effective !== b.effective) return a.effective ? 1 : -1;
  return (a.name || a.email || '').localeCompare(b.name || b.email || '');
});

const holderChip = (h) => {
  const dead = !h.effective;
  const why = !h.userActive ? 'account deactivated' : (h.staffLeftOn ? `left ${formatDate(h.staffLeftOn)}` : null);
  const source = h.direct ? 'granted outside any role' : ((h.viaRoles || []).length ? `via ${h.viaRoles.join(', ')}` : null);
  // The chip carries the name alone; email, source and why live in the tooltip.
  const tip = [h.name || null, h.email, source, why].filter(Boolean).join('\n');
  return (
    <span
      key={h.userId}
      title={tip}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs mr-1.5 mb-1 cursor-default ${
        dead ? 'bg-slate-100 text-slate-400 line-through'
          : h.direct ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-700'
      }`}
    >
      {h.name || h.email}
    </span>
  );
};

const SettingsPermissionReference = () => {
  const [permissions, setPermissions] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [activeSub, setActiveSub] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/permission-coverage`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
        if (!cancelled) {
          setPermissions(data.permissions || []);
          setExceptions(data.exceptions || []);
          setGroups(data.groups || []);
          setError(null);
        }
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Tabs are derived from the registry order, so a new group appears here the
  // moment it is added server-side — nothing to keep in step by hand.
  const tabs = useMemo(() => {
    const order = [...groups, 'Other'];
    const out = [];
    order.forEach((group) => {
      const rows = permissions.filter((p) => p.group === group);
      if (rows.length === 0) return;
      const { top, sub } = splitGroup(group);
      let tab = out.find((t) => t.name === top);
      if (!tab) { tab = { name: top, rows: [], subs: [] }; out.push(tab); }
      tab.rows = tab.rows.concat(rows);
      if (sub) tab.subs.push({ name: sub, group, rows });
    });
    return out;
  }, [groups, permissions]);

  const tab = tabs.find((t) => t.name === activeTab) || tabs[0] || null;
  const sub = tab && tab.subs.length > 0
    ? (tab.subs.find((s) => s.name === activeSub) || tab.subs[0])
    : null;
  const rows = sub ? sub.rows : (tab ? tab.rows : []);

  const pickTab = (name) => { setActiveTab(name); setActiveSub(null); };
  const brokenIn = (list) => list.filter((p) => p.status === 'none').length;

  const broken = permissions.filter((p) => p.status === 'none');
  const stale = permissions.filter((p) => p.status === 'warning');

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            Permissions
            <InfoTip text="To change who holds what, use Accounts; for role defaults, use Roles." />
          </h1>
        </div>
        <ResultCount count={rows.length} noun="permission" loading={loading} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {/* Only speaks up when something needs doing — a clean page says so by
          showing nothing here. */}
      {!loading && !error && (broken.length > 0 || stale.length > 0) && (
        <div className={`rounded-lg border p-4 text-sm mb-5 ${
          broken.length > 0 ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}
        >
          <div>
            {broken.length > 0 && (
              <p className="font-semibold">
                {broken.length} permission{broken.length === 1 ? '' : 's'} with NO effective holder:{' '}
                {broken.map((p) => p.label).join('; ')}
              </p>
            )}
            {stale.length > 0 && (
              <p className={broken.length > 0 ? 'mt-1' : 'font-semibold'}>
                {stale.length} permission{stale.length === 1 ? '' : 's'} carrying stale grants (deactivated accounts or staff who left).
              </p>
            )}
          </div>
        </div>
      )}

      <div className="border-b border-slate-200 mb-4 flex flex-wrap gap-1">
        {tabs.map((t) => {
          const Icon = TAB_ICONS[t.name] || BookOpen;
          const active = tab && tab.name === t.name;
          const bad = brokenIn(t.rows);
          return (
            <button
              key={t.name}
              type="button"
              onClick={() => pickTab(t.name)}
              aria-current={active ? 'page' : undefined}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.name}
              <span className={`ml-0.5 text-xs font-semibold ${active ? 'text-blue-500' : 'text-slate-400'}`}>{t.rows.length}</span>
              {bad > 0 && (
                <span title={`${bad} with no effective holder`} className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-100 text-red-700 text-[11px] font-bold">{bad}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab && tab.subs.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {tab.subs.map((s) => {
            const active = sub && sub.name === s.name;
            const bad = brokenIn(s.rows);
            return (
              <button
                key={s.name}
                type="button"
                onClick={() => setActiveSub(s.name)}
                aria-current={active ? 'page' : undefined}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                {s.name}
                <span className={`text-xs font-semibold ${active ? 'text-blue-500' : 'text-slate-400'}`}>{s.rows.length}</span>
                {bad > 0 && (
                  <span title={`${bad} with no effective holder`} className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-100 text-red-700 text-[11px] font-bold">{bad}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm table-scroll">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className={`${THEAD_CLS} text-left`}>Permission</th>
              <th className={`${THEAD_CLS} text-left`}>Covers</th>
              <th className={`${THEAD_CLS} text-left`}>Held By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => {
              const st = STATUS[p.status] || STATUS.ok;
              const sorted = sortHolders(p.holders || []);
              // Over the limit the names give way to a count — except the
              // stale ones, which stay visible because they are the to-do.
              const tooMany = sorted.length > NAME_LIMIT;
              const named = tooMany ? sorted.filter((h) => !h.effective) : sorted;
              return (
                <tr key={p.key} className={`align-top ${st.row || 'hover:bg-slate-50'}`}>
                  <td className="px-4 py-2.5 w-72">
                    <div className="font-medium text-slate-800">{p.label}</div>
                    <div className="font-mono text-xs text-slate-500 mt-0.5">{p.key}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.badge}`}>
                        {p.status === 'none' ? st.label : `${p.effectiveCount} effective`}
                      </span>
                      {p.status === 'none' && p.flow && p.pending > 0 && (
                        <span className="text-xs font-semibold text-red-700">
                          {p.pending} request{p.pending === 1 ? '' : 's'} stuck
                        </span>
                      )}
                      {p.adminOnly && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700"
                          title="Comes with the Admin role only — it cannot be added to another role or ticked on an account"
                        >
                          Admin role only
                        </span>
                      )}
                    </div>
                    {p.adminOnly && (
                      <div className="mt-1 text-xs text-slate-500">Default for the Admin role; not assignable to other roles or accounts.</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{p.covers || '—'}</td>
                  <td className="px-4 py-2.5 w-52">
                    {sorted.length === 0
                      ? <span className="text-slate-500 italic text-xs">No one holds this permission</span>
                      : (
                        <>
                          {tooMany && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs mr-1.5 mb-1 bg-slate-100 text-slate-700 font-medium">
                              {sorted.length} people
                            </span>
                          )}
                          {named.map(holderChip)}
                        </>
                      )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No permissions in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {!loading && !error && (
        <section className="mt-8">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Granted Outside Roles</h2>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
            {exceptions.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-500">
                No account holds a permission outside its roles — every grant comes from a role.
              </p>
            ) : exceptions.map((x) => {
              const dead = !x.effective;
              const why = !x.userActive ? 'account deactivated' : (x.staffLeftOn ? `left ${formatDate(x.staffLeftOn)}` : null);
              return (
                <div key={x.userId} className="px-4 py-3 flex flex-wrap items-start gap-3">
                  <div className="w-72 shrink-0">
                    <div className={`text-sm font-medium ${dead ? 'text-slate-400 line-through' : 'text-slate-800'}`} title={x.email}>
                      {x.name || x.email}
                      {why && <span className="ml-1 no-underline text-xs text-amber-600">({why})</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(x.roles || []).length ? (x.roles || []).join(', ') : 'no roles'}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[14rem]">
                    {x.permissions.map((p) => (
                      <span
                        key={p.key}
                        title={p.key}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs mr-1.5 mb-1 bg-amber-50 text-amber-800 border border-amber-200"
                      >
                        {p.label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default SettingsPermissionReference;
